import React, { useState, useEffect } from "react";
import { Plus, Pencil, Trash2, X, Save } from "lucide-react";
import api from "../api/client";

function TemplateForm({ template, websites, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: template?.name || "",
    brand_voice: template?.brand_voice || "",
    structure_template: template?.structure_template || "",
    website: template?.website || "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, website: form.website || null };
      if (template?.id) {
        await api.put(`/content/templates/${template.id}/`, payload);
      } else {
        await api.post("/content/templates/", payload);
      }
      onSave();
    } catch {}
    setSaving(false);
  };

  return (
    <form onSubmit={handleSubmit} className="bg-dark-300 border border-dark-50 rounded-xl p-5 space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-semibold text-gray-300">
          {template?.id ? "Edit Template" : "New Template"}
        </h3>
        <button type="button" onClick={onCancel} className="text-gray-500 hover:text-white">
          <X size={16} />
        </button>
      </div>

      <div>
        <label className="block text-xs text-gray-400 mb-1">Name</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          placeholder='e.g. "Service Page", "Blog Post"'
          className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50"
          required
        />
      </div>

      <div>
        <label className="block text-xs text-gray-400 mb-1">Website (or Global)</label>
        <select
          value={form.website}
          onChange={(e) => setForm({ ...form, website: e.target.value })}
          className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50"
        >
          <option value="">Global (all sites)</option>
          {websites.map((w) => (
            <option key={w.id} value={w.id}>{w.name} ({w.domain})</option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs text-gray-400 mb-1">Brand Voice</label>
        <textarea
          value={form.brand_voice}
          onChange={(e) => setForm({ ...form, brand_voice: e.target.value })}
          placeholder="Describe the tone, style, and personality of the content..."
          rows={3}
          className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-copper/50 resize-none"
        />
      </div>

      <div>
        <label className="block text-xs text-gray-400 mb-1">Structure Template</label>
        <textarea
          value={form.structure_template}
          onChange={(e) => setForm({ ...form, structure_template: e.target.value })}
          placeholder={"H1: [Service] in [Location]\nH2: What is [Service]?\nH2: Benefits of [Service]\nH2: Why Choose [Brand]?\nH2: FAQ\nH2: Contact Us"}
          rows={6}
          className="w-full bg-dark border border-dark-50 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-copper/50 resize-none"
        />
      </div>

      <button
        type="submit"
        disabled={saving}
        className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
      >
        <Save size={14} />
        {saving ? "Saving..." : "Save Template"}
      </button>
    </form>
  );
}

export default function ContentTemplates({ websites }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const fetchTemplates = () => {
    setLoading(true);
    api
      .get("/content/templates/")
      .then((res) => setTemplates(res.data.results || res.data))
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/content/templates/${id}/`);
      fetchTemplates();
    } catch {}
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Content Templates</h2>
        <button
          onClick={() => {
            setEditingTemplate(null);
            setShowForm(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-copper hover:bg-copper-600 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus size={14} />
          New Template
        </button>
      </div>

      {showForm && (
        <TemplateForm
          template={editingTemplate}
          websites={websites}
          onSave={() => {
            setShowForm(false);
            setEditingTemplate(null);
            fetchTemplates();
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingTemplate(null);
          }}
        />
      )}

      {loading ? (
        <div className="animate-pulse space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-dark-300 rounded-xl" />
          ))}
        </div>
      ) : !templates.length ? (
        <div className="bg-dark-300 border border-dark-50 rounded-xl p-12 text-center text-gray-500">
          No templates yet. Create one to standardize your content output.
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => (
            <div key={t.id} className="bg-dark-300 border border-dark-50 rounded-xl p-5">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-medium text-gray-200">{t.name}</h3>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {t.website_domain || "Global"} · Updated {new Date(t.updated_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingTemplate(t);
                      setShowForm(true);
                    }}
                    className="p-1.5 text-gray-500 hover:text-white transition-colors"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="p-1.5 text-gray-500 hover:text-red-400 transition-colors"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              {t.brand_voice && (
                <div className="mt-3 text-sm text-gray-400 line-clamp-2">
                  {t.brand_voice}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
